import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import envs from 'envs';
import PropTypes from 'prop-types';

import { useFetch } from 'hooks';

// Components
import { Mui } from 'components';
import { InputSearch } from 'components/common';
import { Drawer, Stack } from 'components/layouts';

// Local components
import * as api from '../api';
import { dedupeArticlesBySection, searchArticles } from '../utils';
import ArticlesList, { ArticlesListSkeleton } from './ArticlesList';
import CustomerSuccessSection from './CustomerSuccessSection';
import FeaturedList from './FeaturedList';

const useStyles = Mui.makeStyles(theme => ({
  section: {
    backgroundColor: theme.palette.brand.grey[200],
    borderRadius: theme.shape.borderRadius,
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
  },
  link: {
    color: theme.palette.primary.main,
  },
}));

function HelpCenterDrawer({ open, onClose, context, slug, customerSuccesses, featured }) {
  // Hooks
  const classes = useStyles();

  // States
  const [search, setSearch] = useState('');

  // Constants
  const isPlatformUser = slug === envs.APPLICATION_SLUG;
  const articlesUrl = `/${context}/${slug}/support/platform-articles`;
  const hasFeatured = !!featured?.items?.length;

  // Fetch
  const { data } = useFetch([
    open && slug && api.endpoints.list(context, slug),
    { ordering: 'ordering', is_active: true },
  ]);

  // Constants
  const trimmedSearch = search.trim();
  const displayedArticles = trimmedSearch
    ? searchArticles(data?.results, trimmedSearch)
    : dedupeArticlesBySection(data?.results);
  const isLoading = open && !data;
  const showArticles = !hasFeatured || !!trimmedSearch;

  return (
    <Drawer
      open={!!open}
      onClose={onClose}
      title={
        <Mui.Box display="flex" alignItems="center" gridGap={8}>
          <Mui.Icon>help_outline</Mui.Icon>
          Central de Ajuda
        </Mui.Box>
      }>
      <Mui.Box px={3} mt={-4}>
        <Stack>
          {customerSuccesses?.length > 0 && (
            <CustomerSuccessSection customerSuccesses={customerSuccesses} />
          )}
          {hasFeatured && <FeaturedList title={featured.title} items={featured.items} />}
          <Mui.Box>
            {!hasFeatured && (
              <Mui.Box mb={1}>
                <Mui.Typography variant="h5" style={{ fontWeight: 700 }}>
                  Artigos e materiais de apoio
                </Mui.Typography>
              </Mui.Box>
            )}
            <InputSearch
              placeholder={hasFeatured ? 'Buscar nos artigos' : 'Buscar artigos, tutoriais...'}
              defaultValue={search}
              count={isLoading ? null : displayedArticles.length}
              onSearch={({ search: newSearch }) => setSearch(newSearch)}
            />
            <Mui.Box mt={1}>
              <Mui.Link
                component={RouterLink}
                to={articlesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}>
                Ver todos os artigos
              </Mui.Link>
            </Mui.Box>
          </Mui.Box>
          {showArticles && (
            <Mui.Box px={2} py={1} className={classes.section}>
              <Mui.Typography variant="subtitle2" color="textSecondary">
                {trimmedSearch ? 'Resultados da busca' : 'Artigos populares'}
              </Mui.Typography>
              <Mui.Paper variant="outlined" style={{ marginTop: 8 }}>
                {isLoading ? (
                  <ArticlesListSkeleton />
                ) : (
                  <ArticlesList
                    articles={displayedArticles}
                    context={context}
                    slug={slug}
                    search={trimmedSearch}
                  />
                )}
              </Mui.Paper>
            </Mui.Box>
          )}
        </Stack>
        {!isPlatformUser && (
          <Mui.Box display="flex" gridGap={8} mt={2} pt={2} pb={2} className={classes.footer}>
            <Mui.Button
              variant="outlined"
              color="primary"
              fullWidth
              component={RouterLink}
              to={`/${context}/${slug}/support/platform-tickets`}
              target="_blank"
              rel="noopener noreferrer">
              Meus chamados
            </Mui.Button>
            <Mui.Button
              variant="contained"
              color="primary"
              fullWidth
              component={RouterLink}
              to={`/${context}/${slug}/support/platform-tickets?open=1`}
              target="_blank"
              rel="noopener noreferrer">
              Abrir chamado
            </Mui.Button>
          </Mui.Box>
        )}
      </Mui.Box>
    </Drawer>
  );
}

HelpCenterDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.oneOf(['sellers', 'institutions']),
  slug: PropTypes.string,
  customerSuccesses: PropTypes.array,
  featured: PropTypes.shape({
    title: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }),
};

export default HelpCenterDrawer;
